import api from "@/api";

export default {
  namespaced: true,
  state: () => ({
    announcements: [],
    currentAnnouncement: null,
    isLoading: false,
    error: null,
    filters: {  
      search: "",
      status: null,
    },
    pagination: { currentPage: 1, perPage: 10 },
  }),

  mutations: {
    SET_ANNOUNCEMENTS(state, announcementsArray) {
      state.announcements = Array.isArray(announcementsArray) ? announcementsArray : [];
    },

    SET_ANNOUNCEMENT(state, announcement) {
      state.currentAnnouncement = announcement;
    },

    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    SET_PAGINATION(state, pagination) {
      state.pagination = pagination;
    },

    SET_FILTERS(state, filters) {
      state.filters = filters;
    },

    SET_PER_PAGE(state, perPage) {
      state.pagination.perPage = perPage;
    },

    ADD_ANNOUNCEMENT(state, announcement) {
      state.announcements.unshift(announcement);
    },

    UPDATE_ANNOUNCEMENT(state, updatedAnnouncement) {
      const index = state.announcements.findIndex(a => a.id === updatedAnnouncement.id);
      if (index !== -1) {
        state.announcements.splice(index, 1, updatedAnnouncement);
      }
    },

    DELETE_ANNOUNCEMENT(state, announcementId) {
      state.announcements = state.announcements.filter(a => a.id !== announcementId);
    },
  },

  actions: {
    async fetchAnnouncements({ commit, state }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const params = {};
        if (state.filters) {
          if (state.filters.search?.trim()) params.search = state.filters.search.trim();
          if (state.filters.status) params.status = state.filters.status;
        }

        const response = await api.get("/announcements", { params });

        commit("SET_ANNOUNCEMENTS", response.data?.data ?? []);

      } catch (error) {
        console.error("Failed to fetch announcements:", error);
        commit("SET_ERROR", error?.response?.data?.message || error.message || "Failed to load announcements");
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchAnnouncement({ commit }, id) {
      commit("SET_LOADING", true);
      try {
        const response = await api.get(`/announcements/${id}`);
        commit("SET_ANNOUNCEMENT", response.data?.data ?? response.data);
        return response.data?.data ?? response.data;
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async createAnnouncement({ commit }, formData) {
      commit("SET_LOADING", true);
      try {
        const response = await api.post("/announcements", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        commit("ADD_ANNOUNCEMENT", response.data?.data ?? response.data);
        return response.data?.data ?? response.data;
      } catch (error) {
        if (error.response?.status === 422) {
          throw error.response.data.errors;
        }
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async updateAnnouncement({ commit }, { id, announcementData }) {
  commit("SET_LOADING", true);
  try {
    const formData = new FormData();

    formData.append('title', announcementData.get('title'));
    formData.append('description', announcementData.get('description'));
    formData.append('date', announcementData.get('date'));
    formData.append('status', announcementData.get('status'));

    formData.append('audience_type', announcementData.get('audience_type'));
    formData.append('class_id', announcementData.get('class_id') ?? '');
    formData.append('class_group_id', announcementData.get('class_group_id') ?? '');

    const image = announcementData.get('image');
    if (image instanceof File) {
      formData.append('image', image);
    }

    formData.append('_method', 'PUT');

    const response = await api.post(`/announcements/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const updated = response.data?.data ?? response.data;
    commit("UPDATE_ANNOUNCEMENT", updated);
    return updated;
  } catch (error) {
    if (error.response?.status === 422) {
      const formattedErrors = {};
      Object.entries(error.response.data.errors).forEach(([key, value]) => {
        formattedErrors[key] = value[0];
      });
      throw formattedErrors;
    }
    commit("SET_ERROR", error.response?.data?.message || error.message);
    throw error;
  } finally {
    commit("SET_LOADING", false);
  }
},

    async deleteAnnouncement({ commit }, id) {
      commit("SET_LOADING", true);
      try {
        await api.delete(`/announcements/${id}`);
        commit("DELETE_ANNOUNCEMENT", id);
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.message || error.message);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async setFilters({ commit, dispatch, state }, filters) {
      commit("SET_FILTERS", filters);
      commit("SET_PAGINATION", { ...state.pagination, currentPage: 1 });
      await dispatch("fetchAnnouncements");
    },

    async setPerPage({ commit, dispatch, state }, perPage) {
      commit("SET_PER_PAGE", perPage);
      commit("SET_PAGINATION", { ...state.pagination, currentPage: 1 });
      await dispatch("fetchAnnouncements");
    },
  },

  getters: {
    announcements: (state) => state.announcements,
    activeAnnouncements: (state) => state.announcements.filter(a => a.status === 'active'),
    announcement: (state) => state.currentAnnouncement,
    isLoading: (state) => state.isLoading,
    error: (state) => state.error,
    pagination: (state) => state.pagination,
    filters: (state) => state.filters,
  },
};
